import React from "react";
import { PILLARS } from "@nexus/contracts";
import type { IndexRankingEntry } from "../types";
import { PILLAR_METADATA } from "../types";
import { Award, AlertTriangle, ChevronRight, HelpCircle, CheckCircle2 } from "lucide-react";

interface TechPowerIndexProps {
  ranking: IndexRankingEntry[];
  selectedEntityId: string | null;
  onSelectEntity: (entityId: string) => void;
  onOpenExplain: (entityId: string) => void;
  isLoading: boolean;
}

const COUNTRY_FLAGS: Record<string, string> = {
  us: "🇺🇸",
  cn: "🇨🇳",
  tw: "🇹🇼",
  kr: "🇰🇷",
  jp: "🇯🇵",
  ru: "🇷🇺",
  kp: "🇰🇵",
  eu: "🇪🇺",
};

export const TechPowerIndex: React.FC<TechPowerIndexProps> = ({
  ranking,
  selectedEntityId,
  onSelectEntity,
  onOpenExplain,
  isLoading,
}) => {
  return (
    <div className="floating-panel right-panel">
      <div className="panel-section" style={{ paddingBottom: "12px" }}>
        <div className="section-title">
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Award size={14} color="#38bdf8" />
            <span style={{ color: "#e2e8f0" }}>TECH POWER INDEX (COMPOSITE)</span>
          </div>
          <span className="font-mono" style={{ fontSize: "11px", fontWeight: 700, color: "var(--accent-cyan)" }}>
            SNAPSHOT: 2026-H1
          </span>
        </div>
        <p style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.45 }}>
          4대 핵심 기둥(AI·반도체·에너지·로보틱스) 정규화 점수 합성 지수. 항목을 클릭하면{" "}
          <strong style={{ color: "#38bdf8" }}>투명한 산출 근거(Explain)</strong>를 확인합니다.
        </p>
      </div>

      <div className="ranking-table-container">
        {isLoading ? (
          <div style={{ padding: "30px", textAlign: "center", color: "var(--text-muted)" }}>
            <div className="font-mono">계산 엔진에서 지수 데이터를 로딩 중...</div>
          </div>
        ) : (
          <table className="ranking-table">
            <thead>
              <tr>
                <th style={{ width: "36px", textAlign: "center" }}>순위</th>
                <th>국가 / 실체</th>
                <th style={{ width: "110px" }}>기둥별 분포</th>
                <th style={{ width: "70px", textAlign: "right" }}>합산 점수</th>
                <th style={{ width: "28px" }}></th>
              </tr>
            </thead>
            <tbody>
              {ranking.map((entry) => {
                const isSelected = selectedEntityId === entry.entityId;
                const flag = COUNTRY_FLAGS[entry.entityId] || "🌐";
                const isTop3 = entry.rank <= 3;

                return (
                  <tr
                    key={entry.entityId}
                    className={`ranking-row ${isSelected ? "selected" : ""}`}
                    onClick={() => {
                      onSelectEntity(entry.entityId);
                      onOpenExplain(entry.entityId);
                    }}
                    title={`${entry.entityNameKo}의 종합 점수 및 지표 산출 근거(Explain) 확인`}
                  >
                    <td style={{ textAlign: "center" }}>
                      <span className={`rank-number ${isTop3 ? "top3" : ""}`}>
                        {entry.rank}
                      </span>
                    </td>

                    <td>
                      <div className="entity-cell">
                        <span className="entity-flag">{flag}</span>
                        <div className="entity-names">
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span className="entity-name-ko" style={{ color: isSelected ? "#38bdf8" : "#ffffff" }}>
                              {entry.entityNameKo}
                            </span>
                            {isSelected && (
                              <span style={{ fontSize: "9px", background: "#0284c7", color: "#ffffff", padding: "1px 5px", borderRadius: "3px", fontWeight: 700 }}>
                                선택됨
                              </span>
                            )}
                            {entry.lowConfidence && (
                              <span
                                className="badge-low-conf font-mono"
                                title={`결측률 ${Math.round((1 - entry.coverage) * 100)}%: 데이터 커버리지 부족 경고`}
                              >
                                <AlertTriangle size={9} style={{ display: "inline", marginRight: "2px" }} />
                                저신뢰
                              </span>
                            )}
                          </div>
                          <span className="entity-name-en" style={{ color: isSelected ? "#93c5fd" : "var(--text-muted)" }}>
                            {entry.entityNameEn} ({entry.entityId.toUpperCase()})
                          </span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="pillar-mini-bars" title="AI / CHIP / ENERGY / ROBOTICS 기둥 점수">
                        {PILLARS.map((pillar) => {
                          const meta = PILLAR_METADATA[pillar];
                          const score = entry.pillarScores[pillar];
                          const heightPct = score !== null ? Math.min(100, Math.max(8, score)) : 4;

                          return (
                            <div
                              key={pillar}
                              className="pillar-mini-bar"
                              style={{
                                opacity: score === null ? 0.3 : 1,
                              }}
                              title={`${meta.nameKo} (${pillar}): ${score !== null ? score.toFixed(1) : "결측"}`}
                            >
                              <div
                                className="pillar-mini-fill"
                                style={
                                  {
                                    "--pillar-color": meta.color,
                                    height: `${heightPct}%`,
                                  } as React.CSSProperties
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    </td>

                    <td style={{ textAlign: "right" }}>
                      <span className="total-score-badge tabular-nums">
                        {entry.totalScore.toFixed(1)}
                      </span>
                    </td>

                    <td style={{ textAlign: "center", color: isSelected ? "#38bdf8" : "var(--text-disabled)" }}>
                      {isSelected ? <CheckCircle2 size={15} color="#38bdf8" /> : <ChevronRight size={14} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div
        style={{
          padding: "12px 16px",
          borderTop: "1px solid var(--border-subtle)",
          background: "rgba(10, 16, 28, 0.95)",
          fontSize: "11px",
          color: "var(--text-secondary)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <HelpCircle size={13} color="var(--accent-cyan)" />
          <span>점수 클릭 시 세부 지표 및 가중치 분해(Explain) 팝업</span>
        </div>
        <span className="font-mono" style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 600 }}>
          {ranking.length} ENTITIES
        </span>
      </div>
    </div>
  );
};
